// Header with mega-menu of product categories
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, ChevronDown } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "Our Legacy" },
  { to: "/products", label: "Products", hasMega: true },
  { to: "/catalogue", label: "Catalogue" },
  { to: "/manufacturing", label: "Manufacturing" },
  { to: "/dealers", label: "Become a Dealer" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const { site, categories } = useSite();
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    setOpen(false);
    setMega(false);
  }, [loc]);

  return (
    <header data-testid="site-header" className="sticky top-0 z-40 bg-white border-b border-[var(--jt-border)]">
      {/* Top bar */}
      <div className="bg-[var(--jt-ink)] text-white text-xs">
        <div className="jt-container flex items-center justify-between py-2">
          <div className="flex items-center gap-4 font-mono">
            <a href={`tel:${site?.phone || ""}`} className="flex items-center gap-1 hover:text-[var(--jt-primary)]" data-testid="header-phone">
              <Phone className="w-3 h-3" /> {site?.phone || "+91 7428573629"}
            </a>
            <a href={`mailto:${site?.email || ""}`} className="hidden sm:flex items-center gap-1 hover:text-[var(--jt-primary)]" data-testid="header-email">
              <Mail className="w-3 h-3" /> {site?.email || "office@ferreterrotools.com"}
            </a>
          </div>
          <div className="hidden md:flex items-center gap-4 font-mono">
            <span className="text-white/60">Exporting worldwide</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="jt-container flex items-center justify-between gap-3 h-24 lg:h-28">
        <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="header-logo">
          <img
            src="/brand/jhalani-logo.png"
            alt="Jhalani Hand Tools"
            className="h-16 md:h-18 lg:h-24 w-auto"
          />
          <div className="leading-tight hidden sm:block border-l border-[var(--jt-border)] pl-3">
            <div className="font-display font-bold tracking-tight text-base whitespace-nowrap">Hand Tools</div>
            <div className="font-mono text-[0.6rem] tracking-[0.2em] text-[var(--jt-text-muted)] uppercase whitespace-nowrap">Since 1961</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-0 flex-nowrap" data-testid="header-nav">
          {navLinks.map((l) =>
            l.hasMega ? (
              <div
                key={l.to}
                className="relative"
                onMouseEnter={() => setMega(true)}
                onMouseLeave={() => setMega(false)}
              >
                <Link
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className="flex items-center gap-1 px-2 lg:px-2.5 xl:px-3 py-2 text-[0.72rem] lg:text-[0.78rem] xl:text-sm font-display font-bold uppercase tracking-wide hover:text-[var(--jt-primary)] transition-colors whitespace-nowrap"
                >
                  {l.label}
                  <ChevronDown className="w-3 h-3" />
                </Link>
                {mega && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[760px]" data-testid="mega-menu">
                    <div className="bg-white border border-[var(--jt-border)] shadow-xl p-6 grid grid-cols-3 gap-x-6 gap-y-2">
                      {categories.slice(0, 18).map((c) => (
                        <Link
                          key={c.slug}
                          to={`/category/${c.slug}`}
                          className="group flex items-center justify-between py-2 border-b border-[var(--jt-border)] hover:border-[var(--jt-primary)]"
                          data-testid={`mega-cat-${c.slug}`}
                        >
                          <span className="text-sm font-medium truncate group-hover:text-[var(--jt-primary)]">
                            {c.title.split("Manufacturer")[0].split("Hand Tool")[0].trim() || c.title}
                          </span>
                          <span className="font-mono text-[0.65rem] text-[var(--jt-text-muted)]">{String(c.product_count).padStart(2, "0")}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="px-2 lg:px-2.5 xl:px-3 py-2 text-[0.72rem] lg:text-[0.78rem] xl:text-sm font-display font-bold uppercase tracking-wide hover:text-[var(--jt-primary)] transition-colors whitespace-nowrap"
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3 flex-shrink-0">
          <Link to="/contact" className="hidden xl:inline-flex jt-btn jt-btn-primary !py-2 !px-4 !text-xs" data-testid="header-cta">
            Get a Quote
          </Link>
          <button
            className="md:hidden p-2 -mr-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="header-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-[var(--jt-border)] bg-white" data-testid="mobile-menu">
          <div className="jt-container py-4 flex flex-col gap-1">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} className="py-3 font-display font-bold uppercase text-sm border-b border-[var(--jt-border)]">
                {l.label}
              </Link>
            ))}
            <div className="pt-3 grid grid-cols-2 gap-2">
              {categories.slice(0, 8).map((c) => (
                <Link key={c.slug} to={`/category/${c.slug}`} className="font-mono text-xs py-2 text-[var(--jt-text-muted)] hover:text-[var(--jt-primary)]">
                  {c.title.split("Manufacturer")[0].split("Hand Tool")[0].trim() || c.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
